// Pure ES-module port of the Go app's marker declutter
// (gt_simpligps_personal_app/lib/screens/live/marker_collision.dart). Keeps the
// web live map in sync with the app so clustered markers KEEP THEIR SIDE on pan
// and zoom instead of reshuffling.
//
// Why it is stable: map pan (a translation) and zoom (a uniform scale about a
// point) are affine, and affine maps preserve the direction of the vector
// between two points. Each fanned marker's slot is derived from its TRUE bearing
// off the group's anchor, so that bearing is pan-invariant and zoom-continuous:
// each device keeps its side of the cluster; only its leader line stretches.
//
// Layout: the highest-priority non-anchored member (the "primary") and every
// anchored marker (moving or followed) stay on their true point. The rest orbit
// the PRIMARY on a ring of radius >= minDist, so a fanned marker can never cover
// the anchor. Small crowds (<= 4 fanned) keep each marker on its true bearing and
// are nudged apart only enough to not overlap (minimal displacement); larger
// crowds use an even fan that is always separated.
//
// inputs: [{ id:number, x:number, y:number, priority:number, anchored:boolean }]
// returns: { [id]: { x:number, y:number } }  (rounded pixel offset per displaced marker)

export function computeMarkerOffsets(inputs, { minDist = 52, maxOffset = 80 } = {}) {
  const n = inputs.length;
  const offsets = {};
  if (n < 2) return offsets;

  const ox = inputs.map((i) => i.x);
  const oy = inputs.map((i) => i.y);
  const minSq = minDist * minDist;

  const sqTrue = (a, b) => {
    const dx = ox[a] - ox[b];
    const dy = oy[a] - oy[b];
    return dx * dx + dy * dy;
  };

  const seen = new Array(n).fill(false);
  for (let s = 0; s < n; s++) {
    if (seen[s]) continue;
    // Connected group by TRUE screen proximity (BFS over < minDist).
    const queue = [s];
    seen[s] = true;
    const group = [];
    while (queue.length) {
      const c = queue.pop();
      group.push(c);
      for (let j = 0; j < n; j++) {
        if (!seen[j] && sqTrue(c, j) < minSq) {
          seen[j] = true;
          queue.push(j);
        }
      }
    }
    if (group.length < 2) continue;

    // Primary = highest-priority non-anchored member; it (and every anchored
    // marker) stays on its true point. The rest fan out around the primary.
    const ordered = [...group].sort((a, b) => {
      const byPriority = inputs[b].priority - inputs[a].priority;
      return byPriority !== 0 ? byPriority : inputs[a].id - inputs[b].id;
    });
    let primary = null;
    for (const m of ordered) {
      if (!inputs[m].anchored) {
        primary = m;
        break;
      }
    }
    if (primary === null) continue; // all anchored -> nobody is displaced

    const ring = group.filter((m) => m !== primary && !inputs[m].anchored);
    if (ring.length === 0) continue;

    const ax = ox[primary];
    const ay = oy[primary];

    // Each ring marker's bearing off the primary keeps its real side (stable
    // per-id angle if it sits on the primary). Bearings are affine-invariant.
    const beta = {};
    for (const m of ring) {
      const dx = ox[m] - ax;
      const dy = oy[m] - ay;
      const r = Math.sqrt(dx * dx + dy * dy);
      beta[m] = r < 0.5 ? stableAngle(inputs[m].id) : Math.atan2(dy, dx);
    }
    ring.sort((a, b) => {
      const byBearing = beta[a] - beta[b];
      return byBearing !== 0 ? byBearing : inputs[a].id - inputs[b].id;
    });
    const k = ring.length;

    // Ring radius: >= minDist from the primary AND, at even spacing, neighbours
    // 2*R*sin(pi/k) >= minDist apart. For k <= 9 both hold within maxOffset; a
    // 10+ co-located pile clamps to maxOffset and packs tighter (accepted).
    const mutual = k >= 2 ? minDist / (2 * Math.sin(Math.PI / k)) : minDist;
    let radius = Math.max(minDist, mutual);
    if (radius > maxOffset) radius = maxOffset;

    // Minimum gap that keeps two markers minDist apart on a radius-R ring. When
    // it leaves slack under a full even share (2*pi/k) - the common small-crowd
    // case - keep markers on their true bearing and only spread the too-close
    // ones (minimal displacement). Otherwise fan them out evenly.
    const needGap = 2 * Math.asin(clamp(minDist / (2 * radius), 0, 1));
    const evenGap = (2 * Math.PI) / k;
    const slots = ring.map((m) => beta[m]);
    if (k <= 4 && needGap < evenGap - 1e-6) {
      for (let iter = 0; iter < 30; iter++) {
        let moved = false;
        for (let i = 0; i < k; i++) {
          const j = (i + 1) % k;
          let gap = slots[j] - slots[i];
          if (j === 0) gap += 2 * Math.PI; // wrap-around pair
          if (gap < needGap - 1e-9) {
            const push = (needGap - gap) / 2;
            slots[i] -= push;
            slots[j] += push;
            moved = true;
          }
        }
        if (!moved) break;
      }
    } else {
      let sumSin = 0;
      let sumCos = 0;
      for (const m of ring) {
        sumSin += Math.sin(beta[m]);
        sumCos += Math.cos(beta[m]);
      }
      const base = Math.atan2(sumSin, sumCos) - ((k - 1) / 2) * evenGap;
      for (let i = 0; i < k; i++) slots[i] = base + i * evenGap;
    }

    for (let i = 0; i < k; i++) {
      const m = ring[i];
      // Ring position is at most `radius` (<= maxOffset) from the primary, so
      // markers stay bounded near the cluster; no separate offset cap needed.
      const rdx = Math.round(ax + radius * Math.cos(slots[i]) - ox[m]);
      const rdy = Math.round(ay + radius * Math.sin(slots[i]) - oy[m]);
      if (rdx !== 0 || rdy !== 0) offsets[inputs[m].id] = { x: rdx, y: rdy };
    }
  }
  return offsets;
}

// Deterministic angle in [0, 2*pi) from a marker id, so exactly-stacked markers
// get a consistent fan slot that never swaps. Math.imul + >>> 0 reproduces the
// Dart `(id * 2654435761) & 0xFFFFFFFF` unsigned low-32-bit hash exactly.
function stableAngle(id) {
  const h = Math.imul(id, 2654435761) >>> 0;
  return (h / 0x100000000) * 2 * Math.PI;
}

function clamp(v, lo, hi) {
  return v < lo ? lo : v > hi ? hi : v;
}
